import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import type { HtmlViewerMessage } from "../../../shared/htmlViewer";
import { HTML_VIEWER_ROUTE } from "../../../shared/reservedRoutes";
import { useSessionTenantEdgeOrigin } from "../../hooks/useSessionTenantOrigin";
import { htmlInspectScript } from "../../../shared/htmlViewer/inspectScript.macro" with { type: "macro" };
import type { LiveFeature } from "../LiveBrowser/feature";

const inspectScript = htmlInspectScript();

// No allow-same-origin: the document gets an opaque origin, so it can never
// reach our cookies, storage, or DOM — pair with LiveBrowser when the caller
// needs an authenticated, same-origin iframe.
const sandbox = "allow-scripts";

export type HtmlViewerHandle = {
  back: () => void;
  forward: () => void;
  refresh: () => void;
};

type Props = {
  html: string;
  baseRoute?: string;
  title?: string;
  className?: string;
  interactive?: boolean;
  // Imperative handle for the interactive variant's browser-style controls.
  apiRef?: Ref<HtmlViewerHandle>;
  inspectFeature?: LiveFeature;
};

// An anonymous viewer for untrusted HTML: the html-viewer harness loads in
// the iframe, the parent posts it a MessagePort, and the html goes over the
// port to be document.write'n — so the content never appears in a URL and
// the harness controls its own origin. The opaque-origin sandbox always
// blocks same-origin access (cookies, storage, our DOM), top-navigation,
// forms, and popups. Inert by default; `interactive` additionally lets the
// user scroll, click, and type in the frame — interaction the page's own
// scripts can observe, so enable it only where the surface accepts rendering
// an interactive untrusted page.
export function HtmlViewer({
  html,
  baseRoute,
  title,
  className,
  interactive,
  apiRef,
  inspectFeature,
}: Props) {
  const edgeOrigin = useSessionTenantEdgeOrigin();
  const src = `${edgeOrigin}${HTML_VIEWER_ROUTE}`;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const portRef = useRef<MessagePort | null>(null);
  const htmlRef = useRef(html);
  const baseRouteRef = useRef(baseRoute);
  const inspectRef = useRef(inspectFeature);
  const detachInspectRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    htmlRef.current = html;
    baseRouteRef.current = baseRoute;
    inspectRef.current = inspectFeature;
  });
  const pendingRenderRef = useRef(false);
  const canInspect = inspectFeature != null;

  // Assigning src (even the same string) reloads the shell, so every html
  // or src change funnels through one load path. document.write in the
  // shell fires a second `load`; pendingRenderRef gates the spurious fire
  // so it doesn't close the port under a healthy render.
  useLayoutEffect(() => {
    pendingRenderRef.current = true;
    if (iframeRef.current) iframeRef.current.src = src;
  }, [html, src, canInspect]);

  const onLoad = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;
    if (!pendingRenderRef.current) return;
    pendingRenderRef.current = false;

    detachInspectRef.current?.();
    portRef.current?.close();
    const channel = new MessageChannel();
    portRef.current = channel.port1;
    iframeWindow.postMessage({ type: "init" }, "*", [channel.port2]);

    const renderMessage: HtmlViewerMessage = {
      type: "render",
      data: inspectRef.current
        ? `${htmlRef.current}<script>${inspectScript}</script>`
        : htmlRef.current,
      baseRoute: baseRouteRef.current,
    };
    channel.port1.postMessage(renderMessage);
    detachInspectRef.current = inspectRef.current?.attach(channel.port1);
  }, []);

  useEffect(
    () => () => {
      detachInspectRef.current?.();
      portRef.current?.close();
    },
    []
  );

  // Reload restores the original artifact: reassigning src reloads the shell
  // (even after an in-page link navigated the frame away), and onLoad re-posts
  // the html over a fresh port.
  const reload = useCallback(() => {
    pendingRenderRef.current = true;
    if (iframeRef.current) iframeRef.current.src = src;
  }, [src]);

  useImperativeHandle(apiRef, () => {
    const post = (message: HtmlViewerMessage) =>
      portRef.current?.postMessage(message);
    return {
      back: () => post({ type: "back" }),
      forward: () => post({ type: "forward" }),
      refresh: reload,
    };
  }, [reload]);

  return (
    <iframe
      ref={iframeRef}
      sandbox={sandbox}
      onLoad={onLoad}
      inert={interactive ? undefined : true}
      title={title}
      className={className}
    />
  );
}
